import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Mail } from 'lucide-react';
import {
  useBookings,
  useBookingPayments,
  useClientOutstandingOrders,
  useUpdateBookingStatus,
} from '../../../../hooks/accommodation';
import { useAppSelector } from '../../../../hooks/useRedux';
import { selectAccommodationProperty } from '../../../../store/accommodation';
import { AccommodationBookingStatus } from '../../../../types/accommodation';
import { apiError, apiSuccess } from '../../../../util/notify';
import {
  exportBookingConfirmationPdf,
  exportCheckinFormPdf,
  exportCheckoutFormPdf,
} from '../../../../services/accommodationDocService';
import { sendBookingStatusEmail } from '../../../../services/accommodationService';
import { ExtendStayModal } from './ExtendStayModal';
import { EarlyCheckoutModal } from './EarlyCheckoutModal';

const NEXT_STATUSES: Partial<
  Record<AccommodationBookingStatus, AccommodationBookingStatus[]>
> = {
  pending: ['confirmed', 'cancelled'],
  confirmed: ['checked_in', 'cancelled', 'no_show'],
  checked_in: ['checked_out'],
};

/** Statuses for which email notifications make sense */
const EMAIL_STATUSES: AccommodationBookingStatus[] = [
  'confirmed',
  'checked_in',
  'checked_out',
  'cancelled',
  'no_show',
];

/** Document availability by booking status */
const DOCS_BY_STATUS: Record<
  AccommodationBookingStatus,
  ('confirmation' | 'checkin' | 'checkout')[]
> = {
  pending: ['confirmation'],
  confirmed: ['confirmation'],
  checked_in: ['confirmation', 'checkin'],
  checked_out: ['confirmation', 'checkin', 'checkout'],
  cancelled: [],
  no_show: [],
};

export const BookingSidebarActions: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const property = useAppSelector(selectAccommodationProperty);
  const { data: bookings = [] } = useBookings(property?.id);
  const booking = bookings.find((b) => b.id === bookingId) ?? null;

  const { data: payments = [] } = useBookingPayments(bookingId);
  const { data: outstandingOrders = [] } = useClientOutstandingOrders(
    booking?.client?.id ?? null,
  );
  const updateBookingStatus = useUpdateBookingStatus();

  const [isExtendStayModalOpen, setIsExtendStayModalOpen] = useState(false);
  const [isEarlyCheckoutModalOpen, setIsEarlyCheckoutModalOpen] =
    useState(false);
  const [isExporting, setIsExporting] = useState<string | null>(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const isSubmitting = updateBookingStatus.isPending;

  const handleStatusChange = (status: AccommodationBookingStatus) => {
    if (!bookingId) return;
    updateBookingStatus.mutate({ id: bookingId, status });
  };

  const handleSendEmail = async () => {
    if (!booking) return;
    setIsSendingEmail(true);
    try {
      await sendBookingStatusEmail(booking.id, booking.status);
      apiSuccess('Email sent to guest.');
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleExportDoc = async (
    docType: 'confirmation' | 'checkin' | 'checkout',
  ) => {
    if (!booking || !property) return;
    setIsExporting(docType);
    try {
      let result;
      if (docType === 'confirmation') {
        result = await exportBookingConfirmationPdf(
          booking,
          payments,
          property,
        );
      } else if (docType === 'checkin') {
        result = await exportCheckinFormPdf(booking, payments, property);
      } else {
        result = await exportCheckoutFormPdf(
          booking,
          payments,
          property,
          outstandingOrders,
        );
      }

      if (result.cancelled) {
        // User dismissed the save dialog — do nothing
      } else if (result.success) {
        apiSuccess('PDF saved successfully.');
      } else {
        apiError(result.error ?? 'Failed to generate PDF');
      }
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to generate PDF');
    } finally {
      setIsExporting(null);
    }
  };

  if (!booking) return null;

  const nextStatuses = NEXT_STATUSES[booking.status] ?? [];
  const availableDocs = DOCS_BY_STATUS[booking.status] ?? [];
  const canSendEmail = EMAIL_STATUSES.includes(booking.status);
  const hasGuestEmail = !!booking.client?.email;

  return (
    <div className="space-y-4 shrink-0">
      {/* Status actions */}
      {(nextStatuses.length > 0 ||
        booking.status === 'checked_in' ||
        booking.status === 'confirmed') && (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-3.5">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 pb-2 border-b border-gray-100">
            Actions
          </h4>
          <div className="flex flex-col gap-2">
            {nextStatuses.map((s) => (
              <button
                key={s}
                type="button"
                disabled={isSubmitting}
                onClick={() => handleStatusChange(s)}
                className={[
                  'rounded-xl px-4 py-2 text-sm font-medium text-white disabled:opacity-60 transition-colors',
                  s === 'cancelled'
                    ? 'bg-red-500 hover:bg-red-600'
                    : s === 'no_show'
                      ? 'bg-orange-500 hover:bg-orange-600'
                      : 'bg-pos-primary hover:bg-pos-primary/95',
                ].join(' ')}
              >
                {s === 'confirmed' && 'Confirm booking'}
                {s === 'checked_in' && 'Check in'}
                {s === 'checked_out' && 'Check out'}
                {s === 'cancelled' && 'Cancel booking'}
                {s === 'no_show' && 'Mark no-show'}
              </button>
            ))}
            {(booking.status === 'checked_in' ||
              booking.status === 'confirmed') && (
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => setIsExtendStayModalOpen(true)}
                className="rounded-xl border border-pos-primary/30 bg-pos-primary/5 px-4 py-2 text-sm font-medium text-pos-primary hover:bg-pos-primary/10 disabled:opacity-60 transition-colors"
              >
                Extend stay
              </button>
            )}
            {booking.status === 'checked_in' && (
              <button
                type="button"
                onClick={() => setIsEarlyCheckoutModalOpen(true)}
                className="rounded-xl border border-pos-primary/30 bg-pos-primary/5 px-4 py-2 text-sm font-medium text-pos-primary hover:bg-pos-primary/10 disabled:opacity-60 transition-colors"
              >
                Early checkout
              </button>
            )}
          </div>
        </div>
      )}

      {/* Documents */}
      {availableDocs.length > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-3.5">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 pb-2 border-b border-gray-100">
            Documents
          </h4>
          <div className="flex flex-col gap-2">
            {availableDocs.includes('confirmation') && (
              <button
                type="button"
                disabled={isExporting !== null}
                onClick={() => handleExportDoc('confirmation')}
                className="flex items-center gap-2 rounded-xl border border-pos-primary/30 bg-pos-primary/5 px-3 py-2 text-sm font-medium text-pos-primary hover:bg-pos-primary/10 disabled:opacity-50 transition-colors"
              >
                <Download size={14} />
                {isExporting === 'confirmation'
                  ? 'Generating…'
                  : 'Booking Confirmation'}
              </button>
            )}
            {availableDocs.includes('checkin') && (
              <button
                type="button"
                disabled={isExporting !== null}
                onClick={() => handleExportDoc('checkin')}
                className="flex items-center gap-2 rounded-xl border border-pos-primary/30 bg-pos-primary/5 px-3 py-2 text-sm font-medium text-pos-primary hover:bg-pos-primary/10 disabled:opacity-50 transition-colors"
              >
                <Download size={14} />
                {isExporting === 'checkin' ? 'Generating…' : 'Check-in Form'}
              </button>
            )}
            {availableDocs.includes('checkout') && (
              <button
                type="button"
                disabled={isExporting !== null}
                onClick={() => handleExportDoc('checkout')}
                className="flex items-center gap-2 rounded-xl border border-pos-primary/30 bg-pos-primary/5 px-3 py-2 text-sm font-medium text-pos-primary hover:bg-pos-primary/10 disabled:opacity-50 transition-colors"
              >
                <Download size={14} />
                {isExporting === 'checkout' ? 'Generating…' : 'Checkout Form'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Email notification */}
      {canSendEmail && (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-3.5">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 pb-2 border-b border-gray-100">
            Email Guest
          </h4>
          {!hasGuestEmail ? (
            <p className="text-xs text-gray-400 font-medium">
              No email address on file for this guest.
            </p>
          ) : (
            <>
              <p className="text-xs text-gray-500 font-medium leading-relaxed">
                Send a status notification to{' '}
                <span className="font-semibold text-gray-750">
                  {booking.client?.email}
                </span>
              </p>
              <button
                type="button"
                disabled={isSendingEmail}
                onClick={handleSendEmail}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                <Mail size={14} />
                {isSendingEmail
                  ? 'Sending…'
                  : `Send ${booking.status.replace('_', ' ')} email`}
              </button>
            </>
          )}
        </div>
      )}

      <ExtendStayModal
        isOpen={isExtendStayModalOpen}
        booking={booking}
        onClose={() => setIsExtendStayModalOpen(false)}
      />

      <EarlyCheckoutModal
        isOpen={isEarlyCheckoutModalOpen}
        booking={booking}
        onClose={() => setIsEarlyCheckoutModalOpen(false)}
      />
    </div>
  );
};
